import {useNavigate} from 'react-router-dom';
import {GlobeAltIcon, UserIcon} from '@heroicons/react/24/outline';
import EmailChangeModal from './EmailChangeModal';
import EmailVerificationModal from './EmailVerificationModal';
import DeleteAccountModal from './DeleteAccountModal';
import ChangePasswordModal from './ChangePasswordModal';
import UsernameChangeWarningModal from './UsernameChangeWarningModal';
import {useAccountData} from '../../hooks/account/useAccountData';
import {useInteractionStats} from '../../hooks/account/useInteractionStats';
import {useProfileUpdate} from '../../hooks/account/useProfileUpdate';
import {useProfilePictureUpload} from '../../hooks/account/useProfilePictureUpload';
import {useAccountModals} from '../../hooks/account/useAccountModals';
import {AccountStatsCard} from './AccountStatsCard';
import {ProfileIdentityCard} from './ProfileIdentityCard';
import {AccountSecuritySection} from './AccountSecuritySection';
import {DangerZoneSection} from './DangerZoneSection';
import {AccountSkeleton} from '../../components/skeletons/AccountSkeleton';
import config from '../../config';
import {ErrorBoundary} from '../../components/errorBoundary';

/**
 * Account page component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
export const AccountPage = () => {
  const navigate = useNavigate();

  // Business logic hooks (platform-agnostic)
  const { profile, trips, interactions, isLoadingProfile, error } = useAccountData();
  const stats = useInteractionStats(interactions);
  const profileUpdate = useProfileUpdate(profile);
  const pictureUpload = useProfilePictureUpload();
  const modals = useAccountModals();

  // Derived values
  const imageUrl = profile?.userId
    ? `${config.media.cdnUrl}/users/${profile.userId}-thumbnail.jpg?v=${pictureUpload.imageVersion}`
    : '/default-profile-picture.jpg';

  // Loading state
  if (isLoadingProfile) {
    return <AccountSkeleton />;
  }

  // Error state
  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-subtle">
        <div className="text-center p-8 bg-surface rounded-2xl shadow-xl max-w-md w-full mx-4">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-error-subtle mb-4">
            <UserIcon className="h-8 w-8 text-error" />
          </div>
          <h3 className="text-lg  text-main">Profile Not Found</h3>
          <p className="mt-2 text-sm text-muted">
            Could not load your profile information. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center p-2 bg-subtle rounded-full mb-2">
            <GlobeAltIcon className="h-6 w-6 text-brand mr-2" />
            <span className="text-brand  tracking-wide uppercase text-sm">
              Traveler Passport
            </span>
          </div>
          <h1 className="text-4xl  text-main tracking-tight sm:text-5xl">
            Welcome, {profile.username}!
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-muted">
            Manage your travel identity and account settings from your personal base camp.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Passport / Identity Card */}
          <div className="lg:col-span-5 space-y-6">
            <AccountStatsCard
              tripsCount={trips?.length ?? 0}
              totalComments={stats.totalComments}
              totalLikes={stats.totalLikes}
              totalViews={stats.totalViews}
            />
            <ErrorBoundary>
              <ProfileIdentityCard
                username={profileUpdate.username}
                description={profileUpdate.description}
                onUsernameChange={profileUpdate.setUsername}
                onDescriptionChange={profileUpdate.setDescription}
                onSave={profileUpdate.saveProfile}
                isSaving={profileUpdate.isSaving}
                imageUrl={imageUrl}
                isUploadingImage={pictureUpload.isUploading}
                onImageClick={pictureUpload.triggerFileSelect}
                fileInputRef={pictureUpload.fileInputRef}
                onFileChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) pictureUpload.uploadPicture(file);
                }}
              />
            </ErrorBoundary>
          </div>

          {/* Right Column: Account Settings */}
          <div className="lg:col-span-7 space-y-6">
            <AccountSecuritySection
              email={profile.email || ''}
              onChangeEmail={modals.emailChange.open}
              onChangePassword={modals.changePassword.open}
              onSignOut={() => navigate('/signout')}
            />
            <DangerZoneSection onDeleteAccount={modals.deleteAccount.open} />
          </div>
        </div>
      </div>

      {/* Modals */}
      <EmailChangeModal
        open={modals.emailChange.isOpen}
        setOpen={(open) => (open ? modals.emailChange.open() : modals.emailChange.close())}
        onEmailChangeInitiated={modals.handleEmailChangeInitiated}
        currentEmail={profile.email || ''}
      />

      {modals.emailVerification.data && (
        <EmailVerificationModal
          open={modals.emailVerification.isOpen}
          setOpen={(open) => (open ? null : modals.emailVerification.close())}
          newEmail={modals.emailVerification.data.newEmail}
          accessToken={modals.emailVerification.data.accessToken}
          onEmailVerified={modals.handleEmailVerified}
        />
      )}

      <ChangePasswordModal
        isOpen={modals.changePassword.isOpen}
        onClose={modals.handlePasswordChanged}
        userEmail={profile.email || ''}
      />

      <DeleteAccountModal
        open={modals.deleteAccount.isOpen}
        setOpen={(open) => (open ? modals.deleteAccount.open() : modals.deleteAccount.close())}
        currentEmail={profile.email || ''}
        onAccountDeleted={modals.handleAccountDeleted}
      />

      <UsernameChangeWarningModal
        open={profileUpdate.needsConfirmation}
        onConfirm={profileUpdate.confirmAndSave}
        onCancel={() => profileUpdate.setNeedsConfirmation(false)}
        currentUsername={profile.username || ''}
        newUsername={profileUpdate.username}
      />
    </div>
  );
};
